import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { ArrowRight, Palette, Share2, Camera, Megaphone, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { trackEvent } from '../utils/tracking';

const serviceIcons = [Palette, Share2, Camera, Megaphone, RefreshCw];

export const Pricing = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { t } = useLanguage();

  return (
    <section
      id="planos"
      data-testid="pricing-section"
      ref={ref}
      className="py-24 md:py-32 lg:py-40 bg-[#eaddcf]/30"
    >
      <div className="container-main">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">

          {/* LEFT — Text */}
          <div className="text-center lg:text-left">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-xs md:text-sm font-medium uppercase tracking-widest text-[#ff00c7] mb-4 block"
            >
              {t.pricing.badge}
            </motion.span>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 }}
              data-testid="pricing-title"
              className="font-['Syne'] text-3xl md:text-4xl lg:text-5xl font-bold text-[#242328] leading-tight mb-8"
            >
              {t.pricing.title}
            </motion.h2>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-5 text-[#242328]/65 leading-relaxed text-base"
            >
              {t.pricing.paragraphs.map((p, i) => (
                <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
              ))}
              {t.pricing.bullets && (
                <ul className="space-y-1 pt-1">
                  {t.pricing.bullets.map((b, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#ff00c7] flex-shrink-0" />
                      <span className="font-medium text-[#242328]/80">{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </motion.div>
          </div>

          {/* RIGHT — Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.35 }}
          >
            <div
              data-testid="plan-card-custom"
              className="bg-white rounded-2xl p-8 md:p-10 border border-[#242328]/5 shadow-sm hover:shadow-xl transition-all duration-500 text-center"
            >
              <h3 className="font-['Syne'] text-2xl md:text-3xl font-bold text-[#242328] mb-8">
                {t.pricing.customTitle}
              </h3>

              <div className="space-y-4 mb-10">
                {t.pricing.services.map((service, i) => {
                  const Icon = serviceIcons[i] || Palette;
                  return (
                    <div key={i} className="flex items-center gap-4 text-left bg-[#f8f6e2]/50 rounded-xl px-5 py-3.5">
                      <div className="w-9 h-9 rounded-lg bg-[#242328] flex items-center justify-center flex-shrink-0">
                        <Icon className="w-4 h-4 text-[#f8f6e2]" strokeWidth={1.5} />
                      </div>
                      <span className="text-[#242328] text-sm md:text-base font-medium">{service}</span>
                    </div>
                  );
                })}
              </div>

              <Link
                to="/form"
                data-testid="pricing-cta"
                onClick={() => trackEvent('cta_click')}
                className="inline-flex items-center gap-2 bg-[#242328] text-[#f8f6e2] px-8 py-4 rounded-full font-semibold text-base hover:bg-[#ff00c7] transition-colors duration-300"
              >
                {t.pricing.cta}
                <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
              </Link>

              <p className="text-xs text-[#242328]/30 mt-4">{t.cta.buttonMicro}</p>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};
